import { useCallback, useRef, useState } from 'react'
import type { TFunction } from 'i18next'
import type { ImportResponse } from '../../../types'
import { importLicenseCriteriaFromJson } from '../../../services/licensing'

export interface LicenseCriteriaImportFormState {
  isDragging: boolean
  selectedFile: File | null
  isUploading: boolean
  result: ImportResponse | null
  error: string | null
  fileInputRef: React.RefObject<HTMLInputElement>
  handleDragOver: (e: React.DragEvent) => void
  handleDragLeave: (e: React.DragEvent) => void
  handleDrop: (e: React.DragEvent) => void
  handleFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void
  handleUpload: () => Promise<void>
  handleClear: () => void
}

interface UseLicenseCriteriaImportFormArgs {
  onSuccess?: ((result: ImportResponse) => void) | undefined
  onError?: ((error: Error) => void) | undefined
  t: TFunction<'admin'>
}

function isJsonFile(file: File): boolean {
  return file.type === 'application/json' || file.name.endsWith('.json')
}

interface ImportUploadDeps {
  file: File
  onSuccess: UseLicenseCriteriaImportFormArgs['onSuccess']
  onError: UseLicenseCriteriaImportFormArgs['onError']
  t: TFunction<'admin'>
  setIsUploading: React.Dispatch<React.SetStateAction<boolean>>
  setResult: React.Dispatch<React.SetStateAction<ImportResponse | null>>
  setError: React.Dispatch<React.SetStateAction<string | null>>
}

async function executeImportUpload(deps: ImportUploadDeps): Promise<void> {
  const { file, onSuccess, onError, t, setIsUploading, setResult, setError } = deps
  setIsUploading(true)
  setError(null)
  setResult(null)
  try {
    const response = await importLicenseCriteriaFromJson(file)
    setResult(response)
    onSuccess?.(response)
  } catch (err) {
    const message = err instanceof Error ? err.message : t('licensing.errors.failedToImportFile')
    setError(message)
    onError?.(err instanceof Error ? err : new Error(message))
  } finally {
    setIsUploading(false)
  }
}

export function useLicenseCriteriaImportForm(
  args: UseLicenseCriteriaImportFormArgs
): LicenseCriteriaImportFormState {
  const { onSuccess, onError, t } = args
  const [isDragging, setIsDragging] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [result, setResult] = useState<ImportResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const acceptCandidate = useCallback(
    (file: File | undefined) => {
      if (file && isJsonFile(file)) {
        setSelectedFile(file)
        setError(null)
        setResult(null)
      } else {
        setError(t('licensing.errors.pleaseUploadJson'))
      }
    },
    [t]
  )

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault()
      event.stopPropagation()
      setIsDragging(false)
      const files = event.dataTransfer.files
      if (files.length > 0) acceptCandidate(files[0])
    },
    [acceptCandidate]
  )

  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files
      if (files && files.length > 0) acceptCandidate(files[0])
    },
    [acceptCandidate]
  )

  const handleUpload = useCallback(async (): Promise<void> => {
    if (!selectedFile) return
    await executeImportUpload({ file: selectedFile, onSuccess, onError, t, setIsUploading, setResult, setError })
  }, [selectedFile, onSuccess, onError, t])

  const handleClear = useCallback(() => {
    setSelectedFile(null)
    setResult(null)
    setError(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }, [])

  return {
    isDragging, selectedFile, isUploading, result, error, fileInputRef,
    handleDragOver, handleDragLeave, handleDrop, handleFileSelect, handleUpload, handleClear,
  }
}
